<template>
  <div class="call-to-action">
    <div v-if="!includesOnlyDocomo">
      <div class="call-to-action__addButton">
        <h4 class="label">{{ $t('pages.CompaniesStorePost.callToAction.title') }}{{ includesDocomo ? $t('labels.requiredLabel'): $t('labels.optionalLabel') }}</h4>
        <KkTooltip
          class="call-to-action__tooltip"
          :label="$t('pages.CompaniesStorePost.callToAction.tooltip.label')"
          top="25px"
          width="400px"
          left="0"
          font-size="12px"
          :is-visible="showTooltip"
          @showTooltip="showTooltip = true"
          @hideTooltip="showTooltip = false"
        >
          <p class="call-to-action__tooltip__content">
            <span class="call-to-action__tooltip__title">{{ $t('pages.CompaniesStorePost.callToAction.tooltip.title') }}</span><br>
            {{ $t('pages.CompaniesStorePost.callToAction.tooltip.description') }}
          </p>
          <div class="call-to-action__tooltip__icon">
            <KkSiteIcon name="google" class="siteIcon" />
          </div>
          <p class="call-to-action__tooltip__content">
            {{ $t('pages.CompaniesStorePost.callToAction.tooltip.note') }}
          </p>
        </KkTooltip>
      </div>

      <div class="c-select">
        <ValidationProvider v-slot="{ errors, handleChange }" v-model="post.callToAction.actionType" :rules="callToActionTypeRules" :name="$t('pages.CompaniesStorePost.callToAction.title')">
          <label>
            <select v-model="post.callToAction.actionType" name="callToAction" @change="handleChange">
              <option v-for="(action, key) in selectableActionTypes" :key="key" :value="key">
                {{ action }}
              </option>
            </select>
          </label>
          <ValidationError
            id="callToActionType"
            :error="errors[0]"
          />
        </ValidationProvider>
      </div>
      <p v-show="post.callToAction.actionType === 'CALL'" class="call-to-action__linkText u-fsS">{{ $t('pages.CompaniesStorePost.callInfo') }}</p>
    </div>

    <div v-show="isUrlRequired">
      <div class="formLabelWrapper">
        <h4 class="label">{{ $t('pages.CompaniesStorePost.callToAction.link') }}{{ $t('labels.requiredLabel') }}</h4>
        <KkInsertParameter
          :parameters="availableParameters"
          :loading="parameterLoading"
          @insert-parameter="insertParam"
        />
      </div>

      <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="callToActionUrl" :rules="callToActionUrlRules" :name="$t('pages.CompaniesStorePost.callToAction.website')">
        <label>
          <input ref="url" :value="value" type="url" class="c-textField" name="buttonLink" @input="handleChange">
        </label>

        <ValidationError
          id="callToActionUrl"
          :error="errors[0]"
        />
      </ValidationProvider>

      <div class="call-to-action__linkText">
        <KkInfo icon="hint">
          {{ $t('pages.CompaniesStorePost.linkParameter', { beforeURL: "https://example.com/{STORE_CODE}" }) }}
        </KkInfo>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'
import ValidationError from '../ValidationError'
import { KkInsertParameter } from '../../components/projects'
import { KkInfo } from '../ui'
import { KkTooltip, KkSiteIcon } from '../../components/base'

export default {
  name: 'CallToAction',
  components: {
    KkInfo,
    ValidationError,
    KkInsertParameter,
    KkTooltip,
    KkSiteIcon
  },
  props: {
    availableParameters: {
      type: Array,
      default: () => []
    },
    parameterLoading: {
      type: Boolean,
      required: false
    },
  },
  data() {
    return {
      showTooltip: false
    }
  },
  computed: {
    ...mapState('companiesStorePosts', ['post']),
    ...mapGetters('companiesStorePosts', [
      'actionTypes',
    ]),
    callToAction: {
      get() {
        return this.post.callToAction
      },
      set(callToAction) {
        this.updateCallToAction(callToAction)
      },
    },
    callToActionUrl: {
      get() {
        return this.post.callToAction.url
      },
      set(callToActionUrl) {
        this.updateCallToActionUrl(callToActionUrl)
      },
    },
    includesOnlyDocomo() {
      if (!this.post.services) return false
      return this.post.services.length === 1 && this.post.services.includes('docomo')
    },
    includesDocomo() {
      if (!this.post.services) return false
      return this.post.services.includes('docomo')
    },
    selectableActionTypes() {
      // docomoメディア投稿が含まれている場合は、'CALL'は選択不可
      if (this.includesDocomo) {
        return Object.fromEntries(
          Object.entries(this.actionTypes).filter(([key]) => key !== 'CALL')
        )
      }
      return this.actionTypes
    },
    isUrlRequired() {
      // docomoメディア投稿が含まれている場合は必要
      if (this.includesDocomo) return true

      return ['BOOK', 'ORDER', 'SHOP', 'LEARN_MORE', 'SIGN_UP'].includes(this.post.callToAction.actionType)
    },
    callToActionTypeRules() {
      return this.includesDocomo ? 'action_type_required' : ''
    },
    callToActionUrlRules() {
      let rules = ''

      if (this.post.topicType === 'OFFER') return rules

      if (this.isUrlRequired) {
        rules += 'required|'
      }
      rules += 'url'

      return rules
    }
  },
  watch: {
    'post.callToAction.actionType': function (){
      this.resetCallToActionAttributes()
    },
  },
  methods: {
    ...mapMutations('companiesStorePosts', [
      'updateCallToAction',
      'updateCallToActionUrl'
    ]),
    resetCallToActionAttributes() {
      if(!this.isUrlRequired) { this.post.callToAction.url = '' }
    },
    insertParam (parameterName) {
      const pos = this.$refs.url.selectionStart
      const before = this.post.callToAction.url.substring(0, pos)
      const after = this.post.callToAction.url.substring(pos, this.post.callToAction.url.length)
      this.post.callToAction.url = before + parameterName + after
    }
  }
}
</script>

<style lang="scss" scoped>
.call-to-action {
  &__linkText {
    margin-top: 16px;
  }
  &__addButton {
    display: flex;
  }
  &__tooltip {
    margin-left: 8px;
    font-size: 12px;
    &__content{
      white-space: pre-wrap;
    }
    &__title {
      font-weight: bold;
    }
    &__icon {
      .siteIcon {
        margin: 0 auto;
        width: 32px;
        height:32px;
      }
      margin-top: 8px;
      margin-left: 4px;
    }
  }
  .label {
    font-size: 12px;
  }
}
.formLabelWrapper {
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
}
</style>
